import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';

const SENSITIVE_URL_PARAMS = [
	'token',
	'addressLine1',
	'addressLine2',
	'city',
	'zip',
	'company',
	'vatNumber',
	'email',
	'phone',
	'firstName',
	'lastName'
];

/**
 * Removes sensitive parameters from a given URL string.
 * If parsing fails (e.g. invalid URL), the original string is returned untouched.
 */
function sanitizeUrl(urlString) {
	try {
		const url = new URL(urlString, window?.location?.origin || undefined);
		SENSITIVE_URL_PARAMS.forEach((param) => {
			if (url.searchParams.has(param)) {
				url.searchParams.delete(param);
			}
		});
		const sanitizedSearch = url.searchParams.toString();
		const sanitized = `${url.origin}${url.pathname}${sanitizedSearch ? `?${sanitizedSearch}` : ''}`;
		return sanitized;
	} catch (e) {
		return urlString;
	}
}

class SanitizeHttpUrlSpanProcessor {
	onStart() {}

	onEnd(span) {
		const urlAttributeKeys = ['http.url', 'http.target'];
		urlAttributeKeys.forEach((attrKey) => {
			const originalUrl = span.attributes?.[attrKey];
			if (typeof originalUrl === 'string') {
				const sanitizedUrl = sanitizeUrl(originalUrl);
				if (sanitizedUrl !== originalUrl) {
					span.attributes[attrKey] = sanitizedUrl;
				}
			}
		});
	}

	shutdown() {
		return Promise.resolve();
	}

	forceFlush() {
		return Promise.resolve();
	}
}

if (process.env.REACT_APP_TRACING_ENABLED) {
	const collectorExporter = new OTLPTraceExporter({
		url: '/traces',
		concurrencyLimit: 10,
	});

	const provider = new WebTracerProvider({
		resource: new Resource({
			[SemanticResourceAttributes.SERVICE_NAME]: 'op-billing-widget',
			'sumup.org.tribe': 'online-payments',
			'sumup.org.squad': 'billing-subscriptions'
		})
	});

	const fetchInstrumentation = new FetchInstrumentation();
	fetchInstrumentation.setTracerProvider(provider);

	provider.addSpanProcessor(new SanitizeHttpUrlSpanProcessor());
	provider.addSpanProcessor(new BatchSpanProcessor(collectorExporter));
	provider.register();

	registerInstrumentations({
		instrumentations: [
			new DocumentLoadInstrumentation(),
			new UserInteractionInstrumentation(),
			new XMLHttpRequestInstrumentation(),
			fetchInstrumentation,
		],
		tracerProvider: provider,
	});
}